import { Card } from '@/components/ui/card'
import { StatusBadge } from '@/components/common'
import { PUNCH_STATES, type RegularizationDecisionContext } from '@/features/attendance'

/**
 * The claim, next to what the terminals actually recorded.
 *
 * <p>The evidence is the whole point: a single 08:57 punch and no exit is a missing punch;
 * four punches with a gap in the middle is a different conversation.
 */
export function RegularisationContext({ detail }: { detail: RegularizationDecisionContext }) {
  const time = (value: string | null) =>
    value == null ? '—' : new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">The day as it stands</h3>
        <dl className="mt-2 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-[var(--color-muted)]">Recorded in</dt>
            <dd className="tabular-nums">{time(detail.shiftStart)}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted)]">Recorded out</dt>
            <dd className="tabular-nums">{time(detail.shiftEnd)}</dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted)]">Worked</dt>
            <dd className="tabular-nums">
              {detail.currentWorkedMinutes == null ? '—' : `${detail.currentWorkedMinutes} min`}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-[var(--color-muted)]">Status</dt>
            <dd>{detail.currentStatus ? <StatusBadge status={detail.currentStatus} /> : '—'}</dd>
          </div>
        </dl>
        {(detail.holiday || detail.weekOff) && (
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            {detail.holiday ? 'A holiday.' : ''} {detail.weekOff ? 'A week-off day.' : ''}
          </p>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">
          What the terminals recorded
        </h3>
        {detail.punches.length === 0 ? (
          <p className="mt-2 text-sm text-[var(--color-muted)]">
            Nothing at all — which is consistent with the claim, and is why it was raised.
          </p>
        ) : (
          <ul className="mt-2 space-y-1.5 text-sm">
            {detail.punches.map((punch, index) => (
              <li key={`${punch.punchTime}-${index}`} className="flex flex-wrap items-center gap-2">
                <span className="tabular-nums font-medium text-[var(--color-heading)]">
                  {time(punch.punchTime)}
                </span>
                <span className="text-[var(--color-muted)]">
                  {punch.punchState == null ? 'Unknown direction' : PUNCH_STATES[punch.punchState] ?? '—'}
                </span>
                {punch.deviceSerial && (
                  <code className="text-xs text-[var(--color-muted)]">{punch.deviceSerial}</code>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}
